import { toaster } from '@/components/ui/toaster';
import useBulkInviteProgressStore from '@/stores/bulk-invite-progress-store';
import { HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Delay before clearing batch state to allow toast transition */
const BATCH_CLEAR_DELAY_MS = 100;

/** Delay before showing progress toast - avoids flicker for fast processing */
const PROGRESS_TOAST_DELAY_MS = 150;

/** Fixed toast ID to ensure we only have one progress toast */
const PROGRESS_TOAST_ID = 'bulk-invite-progress';

/**
 * Global manager for bulk invite progress toasts.
 * Renders at app level to display progress updates from the store.
 * Note: Pusher subscriptions are managed imperatively in invitation-details-view.tsx
 */
export const BulkInviteProgressManager: React.FC = () => {
  const { t } = useTranslation();

  // Use individual selectors to prevent unnecessary re-renders
  const activeBatch = useBulkInviteProgressStore((state) => state.activeBatch);
  const clearBatch = useBulkInviteProgressStore((state) => state.clearBatch);

  const hasCompletedRef = useRef(false);
  const progressToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressToastShownRef = useRef(false);

  // Reset flags when new batch starts
  useEffect(() => {
    if (activeBatch?.status === 'processing') {
      hasCompletedRef.current = false;
      progressToastShownRef.current = false;
    }
  }, [activeBatch?.batchId, activeBatch?.status]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
      }
      toaster.dismiss(PROGRESS_TOAST_ID);
    };
  }, []);

  // Show/update toast when batch changes
  useEffect(() => {
    // The epilogue owns the display while the modal is open: stay silent so the
    // detailed recap and the aggregate toast never show at once. Ownership
    // transfers to the toast when the modal closes mid-flight.
    if (activeBatch && activeBatch.owner !== 'toast') {
      return;
    }
    if (!activeBatch) {
      // Clear toast and timer when batch is cleared
      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
        progressToastTimerRef.current = null;
      }
      toaster.dismiss(PROGRESS_TOAST_ID);
      return;
    }

    const { total, sent, failed, status } = activeBatch;
    const current = sent + failed;

    // Handle completion - show success toast only
    if (status === 'completed' && !hasCompletedRef.current) {
      hasCompletedRef.current = true;

      // Cancel pending progress toast if not shown yet
      if (progressToastTimerRef.current) {
        clearTimeout(progressToastTimerRef.current);
        progressToastTimerRef.current = null;
      }

      // Dismiss progress toast if it was shown
      if (progressToastShownRef.current) {
        toaster.dismiss(PROGRESS_TOAST_ID);
      }

      // Show completion toast
      const allSucceeded = failed === 0;
      toaster.create({
        title: allSucceeded
          ? t('wishlist.bulkInvite.success.title')
          : t('wishlist.bulkInvite.partialSuccess.title'),
        description: allSucceeded
          ? t('wishlist.bulkInvite.success.description', { count: sent })
          : t('wishlist.bulkInvite.partialSuccess.description', { succeeded: sent, failed }),
        type: allSucceeded ? 'success' : 'warning',
        meta: { closable: true },
      });

      // Clear batch after showing completion toast
      setTimeout(clearBatch, BATCH_CLEAR_DELAY_MS);
      return;
    }

    // For processing state - delay showing progress toast to avoid flicker
    // Only start timer when we've received actual progress (first Pusher event)
    // This avoids showing progress toast during network latency before processing begins
    if (status === 'processing') {
      const hasReceivedProgress = current > 0; // Wait for first Pusher event

      // If toast already shown, update it
      if (progressToastShownRef.current) {
        toaster.dismiss(PROGRESS_TOAST_ID);
        toaster.create({
          id: PROGRESS_TOAST_ID,
          title: t('wishlist.bulkInvite.progress.title'),
          description: (
            <ProgressContent
              current={current}
              total={total}
              sent={sent}
              failed={failed}
              status={status}
            />
          ),
          type: 'loading',
          duration: Infinity,
        });
      } else if (hasReceivedProgress && !progressToastTimerRef.current) {
        // Only schedule showing the toast after we receive the first progress event
        progressToastTimerRef.current = setTimeout(() => {
          progressToastTimerRef.current = null;
          progressToastShownRef.current = true;
          toaster.create({
            id: PROGRESS_TOAST_ID,
            title: t('wishlist.bulkInvite.progress.title'),
            description: (
              <ProgressContent
                current={current}
                total={total}
                sent={sent}
                failed={failed}
                status={status}
              />
            ),
            type: 'loading',
            duration: Infinity,
          });
        }, PROGRESS_TOAST_DELAY_MS);
      }
    }
  }, [activeBatch, t, clearBatch]);

  return null;
};

/**
 * Progress content for the toast
 * Memoized to prevent unnecessary re-renders when props haven't changed
 */
interface ProgressContentProps {
  current: number;
  total: number;
  sent: number;
  failed: number;
  status: 'processing' | 'completed' | 'error';
}

const ProgressContent = memo<ProgressContentProps>(({ current, total, sent, failed, status }) => {
  const { t } = useTranslation();
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <VStack align="stretch" gap={2} w="full" minW="250px">
      <Progress.Root value={percentage} size="sm" colorPalette="primary">
        <Progress.Track>
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>
      <HStack justify="space-between" fontSize="xs" color="fg.muted">
        <Text>
          {status === 'processing'
            ? t('wishlist.bulkInvite.progress.sending', { current, total })
            : t('wishlist.bulkInvite.progress.done', { current, total })}
        </Text>
        <Text>{percentage}%</Text>
      </HStack>
      {(sent > 0 || failed > 0) && (
        <HStack gap={3} fontSize="xs">
          {sent > 0 && (
            <Text color="fg.success">
              {t('wishlist.bulkInvite.progress.succeeded', { count: sent })}
            </Text>
          )}
          {failed > 0 && (
            <Text color="fg.error">
              {t('wishlist.bulkInvite.progress.failed', { count: failed })}
            </Text>
          )}
        </HStack>
      )}
    </VStack>
  );
});

ProgressContent.displayName = 'ProgressContent';

export default BulkInviteProgressManager;
